import { useMemo, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Flex,
  Form,
  FormContext,
  Text,
  useScreenResolution,
} from "dash-ui";
import { useTheme } from "styled-components";

import { useGetPortfolioFolderItemsQuery } from "gql/generated.graphql";
import { ManageMediaKitFormFields } from "containers/portfolio/ManageMediaKitModal/ManageMediaKitModal.constants";

const PortfolioFolderItemsSelect = ({
  portfolioFolderId,
  onBulkAction,
  isLoading,
}: {
  portfolioFolderId?: string;
  onBulkAction?: ({ mode }: { mode: "add" | "remove" }) => void;
  isLoading?: boolean;
}) => {
  const theme = useTheme();
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { t } = useTranslation();
  const { setDefaultFormValues, formState, stateHandlers } =
    useContext(FormContext);

  const { data, loading } = useGetPortfolioFolderItemsQuery({
    fetchPolicy: "network-only",
    variables: {
      portfolioFolderId,
      pagination: { allowLimit: false },
      sort: [{ fieldName: "property.name", sortOrder: 1 }],
    },
    skip: !portfolioFolderId,
  });

  const portfolioItems = useMemo(() => {
    const portfolioItems = data?.getPortfolioFolderItems.collection || [];

    return portfolioItems.map((portfolio) => ({
      label: portfolio.property?.name,
      value: JSON.stringify({
        portfolioId: portfolio._id,
        propertyId: portfolio.property?._id,
      }),
    }));
  }, [data]);

  const setValue = (value: string) => {
    const {
      portfolioId,
      propertyId,
    }: { portfolioId: string; propertyId: string } = JSON.parse(value);

    stateHandlers[ManageMediaKitFormFields.CurrentPortfolioId](portfolioId);
    stateHandlers[ManageMediaKitFormFields.CurrentPropertyId](propertyId);
    stateHandlers[ManageMediaKitFormFields.AddedMedia]([
      ...formState?.addedMedia,
      { portfolioId, mediaIds: [] },
    ]);
    stateHandlers[ManageMediaKitFormFields.RemovedMedia]([
      ...formState?.removedMedia,
      { portfolioId, mediaIds: [] },
    ]);
  };

  const selectedItemsCount = useMemo(
    () =>
      formState.addedMedia
        ? formState.addedMedia.reduce(
            (acc: number, item: any) => acc + item.mediaIds.length,
            0
          )
        : 0,
    [formState.addedMedia]
  );

  useEffect(() => {
    if (portfolioItems.length > 0) {
      const { portfolioId, propertyId } = JSON.parse(
        portfolioItems[0].value.trim()
      );

      setDefaultFormValues({
        currentPortfolioId: portfolioId,
        currentPropertyId: propertyId,
        addedMedia: [{ portfolioId: portfolioId, mediaIds: [] }],
        removedMedia: [{ portfolioId: portfolioId, mediaIds: [] }],
      });
    }
  }, [portfolioItems]);

  return (
    <Form.Group w="100%">
      <Form.Label>{t("pages:propertyPage.propertyName")}</Form.Label>
      <Form.Select
        w="100%"
        value={JSON.stringify({
          portfolioId: formState[ManageMediaKitFormFields.CurrentPortfolioId],
          propertyId: formState[ManageMediaKitFormFields.CurrentPropertyId],
        })}
        options={portfolioItems}
        onChanged={(value) => setValue(value)}
        strictMenuWidth
        isLoading={loading}
      />
      <div className="flex flex-row justify-between items-center mt-2">
        <div className="flex flex-row items-center">
          <Box mr={2}>
            <Text color={theme.colors.n80} smplus>
              {t("pages:mediaKit.numberOfAddedItems")}:
            </Text>
          </Box>
          <Text color={theme.colors.n80} semibold>
            {selectedItemsCount}
          </Text>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            icon="download"
            square={isMobile}
            small={!isMobile}
            medium={isMobile}
            variation="outline"
            px={3}
            isLoading={isLoading || loading}
            onClick={(e) => {
              e.preventDefault();
              onBulkAction?.({ mode: "add" });
            }}>
            Add all files
          </Button>
          <Button
            icon="trash"
            square={isMobile}
            small={!isMobile}
            medium={isMobile}
            variation="outline"
            px={3}
            colorVariation="danger"
            isLoading={isLoading || loading}
            onClick={(e) => {
              e.preventDefault();
              onBulkAction?.({ mode: "remove" });
            }}>
            Remove all files
          </Button>
        </div>
      </div>
    </Form.Group>
  );
};

export default PortfolioFolderItemsSelect;
